import React, { useState, useEffect } from 'react'
import { Form, Button, Row, Col, Image } from 'react-bootstrap'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import FormContainer from '../components/FormContainer'
import Loader from '../components/Loader'
import Message from '../components/Message'
import { createContest, updateContest, getContestDetails } from '../actions/contestActions'
import { CONTEST_CREATE_RESET, CONTEST_UPDATE_RESET, CONTEST_DETAILS_RESET } from '../constants/contestConstants'

function ContestEditScreen() {
    const { id } = useParams()
    const isEditing = Boolean(id)
    const dispatch = useDispatch()
    const navigate = useNavigate()

    const [name, setName] = useState('')
    const [description, setDescription] = useState('')
    const [imageDesktop, setImageDesktop] = useState(null)
    const [imageMobile, setImageMobile] = useState(null)
    const [previewDesktop, setPreviewDesktop] = useState(null)
    const [previewMobile, setPreviewMobile] = useState(null)
    const [mobileMimeType, setMobileMimeType] = useState('')

    const contestDetails = useSelector(state => state.contestDetails)
    const { loading: loadingDetails, error: errorDetails, contest } = contestDetails

    const contestCreate = useSelector(state => state.contestCreate)
    const { loading: loadingCreate, error: errorCreate, success: successCreate } = contestCreate

    const contestUpdate = useSelector(state => state.contestUpdate)
    const { loading: loadingUpdate, error: errorUpdate, success: successUpdate } = contestUpdate

    // 1. Fetch on mount / id change only
    useEffect(() => {
        if (isEditing) {
            dispatch(getContestDetails(id))
        }
        return () => { dispatch({ type: CONTEST_DETAILS_RESET }) }
    }, [id])

    // 2. Populate form once data arrives
    useEffect(() => {
        if (contest && contest.id) {
            setName(contest.name)
            setDescription(contest.description || '')
            setPreviewDesktop(contest.image_desktop || null)
            setPreviewMobile(contest.image_mobile || null)
            if (contest.image_mobile && contest.image_mobile.toLowerCase().endsWith('.mp4')) {
                setMobileMimeType('video/mp4')
            }
        }
    }, [contest])

    // 3. Navigate away on success
    useEffect(() => {
        if (successCreate || successUpdate) {
            dispatch({ type: CONTEST_CREATE_RESET })
            dispatch({ type: CONTEST_UPDATE_RESET })
            navigate('/admin/contests')
        }
    }, [successCreate, successUpdate])

    const handleFileChange = (e, type) => {
        const file = e.target.files[0]
        if (!file) return
        const url = URL.createObjectURL(file)
        if (type === 'desktop') {
            setImageDesktop(file)
            setPreviewDesktop(url)
        } else {
            setImageMobile(file)
            setPreviewMobile(url)
            setMobileMimeType(file.type)
        }
    }

    const submitHandler = (e) => {
        e.preventDefault()
        const formData = new FormData()
        formData.append('name', name)
        formData.append('description', description)
        if (imageDesktop) formData.append('image_desktop', imageDesktop)
        if (imageMobile)  formData.append('image_mobile', imageMobile)

        if (isEditing) {
            dispatch(updateContest(id, formData))
        } else {
            dispatch(createContest(formData))
        }
    }

    return (
        <FormContainer>
            <h2>{isEditing ? 'Edit Contest' : 'Add Contest'}</h2>

            {(loadingDetails || loadingCreate || loadingUpdate) && <Loader />}
            {(errorDetails || errorCreate || errorUpdate) && (
                <Message variant='danger'>{errorDetails || errorCreate || errorUpdate}</Message>
            )}

            <Form onSubmit={submitHandler}>
                <Form.Group className='mb-3' controlId='name'>
                    <Form.Label>Name</Form.Label>
                    <Form.Control
                        type='text'
                        placeholder='e.g. Closest to the Pin'
                        value={name}
                        onChange={e => setName(e.target.value)}
                        required
                    />
                </Form.Group>

                <Form.Group className='mb-4' controlId='description'>
                    <Form.Label>Description <span className='text-muted'>(optional)</span></Form.Label>
                    <Form.Control
                        as='textarea'
                        rows={2}
                        placeholder='Brief description of the contest'
                        value={description}
                        onChange={e => setDescription(e.target.value)}
                    />
                </Form.Group>

                <Row className='mb-4'>
                    <Col md={6}>
                        <Form.Group controlId='imageDesktop'>
                            <Form.Label>Desktop Image</Form.Label>
                            {previewDesktop && (
                                <div className='mb-2'>
                                    <Image src={previewDesktop} alt='desktop preview' fluid style={{ maxHeight: '120px', objectFit: 'contain' }} />
                                </div>
                            )}
                            <Form.Control
                                type='file'
                                accept='image/*'
                                onChange={e => handleFileChange(e, 'desktop')}
                            />
                            <Form.Text className='text-muted'>Full size — used for desktop cards</Form.Text>
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group controlId='imageMobile'>
                            <Form.Label>Mobile Image</Form.Label>
                            {previewMobile && (
                                <div className='mb-2'>
                                    {mobileMimeType === 'video/mp4' ? (
                                        <video src={previewMobile} style={{ maxHeight: '120px', maxWidth: '100%' }} controls muted />
                                    ) : (
                                        <Image src={previewMobile} alt='mobile preview' fluid style={{ maxHeight: '120px', objectFit: 'contain' }} />
                                    )}
                                </div>
                            )}
                            <Form.Control
                                type='file'
                                accept='image/*,video/mp4,.gif'
                                onChange={e => handleFileChange(e, 'mobile')}
                            />
                            <Form.Text className='text-muted'>Supports images, GIF, or MP4</Form.Text>
                        </Form.Group>
                    </Col>
                </Row>

                <Button type='submit' variant='dark'>
                    {isEditing ? 'Save Changes' : 'Create'}
                </Button>
                <Button variant='outline-secondary' className='ms-2' onClick={() => navigate('/admin/contests')}>
                    Cancel
                </Button>
            </Form>
        </FormContainer>
    )
}

export default ContestEditScreen
